<template>
  <div ref="containerRef" />
</template>

<script setup lang="ts">
/**
 * This component creates an isolated Vue app instance for SidepaneCommunication
 * with its own Vuex store, preventing state from leaking to/from other sidepanes.
 *
 * This is necessary because SidepaneCommunication uses Options API with mapGetters/mapState/mapActions
 * which access this.$store (set via app.config.globalProperties), not inject('store').
 * Simply using provide() doesn't work for Options API components.
 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { App } from 'vue'

import { createApp } from '@app/adapters/create-app'
import createCommsStore from '@app/application/communication/sections/communication/store'
import SidepaneCommunication from '@app/application/communication/sections/communication/components/sidepane-communication.vue'
import { useHighlightedCard } from '@app/shared/pinia/highlighted-card'

interface Props {
  contextType: 'account' | 'report/element'
  contextId: number
  ledgerId: number
  showEmptyStateImage?: boolean
  showEmptyState?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showEmptyStateImage: false,
  showEmptyState: false,
})

const containerRef = ref<HTMLElement | null>(null)
let mountedApp: App | null = null

const highlightedCardStore = useHighlightedCard()

function mountCommunicationApp() {
  if (!containerRef.value) return

  // Unmount any existing app
  unmountApp()

  // Create a new Vue app with its own isolated Vuex store
  const app = createApp(SidepaneCommunication, {
    showEmptyStateImage: props.showEmptyStateImage,
    showEmptyState: props.showEmptyState,
  })

  // Create an isolated store with context-specific settings
  const store = createCommsStore({
    contextType: props.contextType,
    contextId: props.contextId,
    contextHrefParam: props.contextId?.toString(),
    ledgerId: props.ledgerId,
  })

  // This sets both app.provide('store', store) AND app.config.globalProperties.$store
  app.use(store)
  app.mount(containerRef.value)
  mountedApp = app

  // Fetch communication data for this context
  store.dispatch('setCommunicationContext', {
    contextType: props.contextType,
    contextId: props.contextId,
    contextHrefParam: props.contextId?.toString(),
  })

  if (window.Silverfin.presentationMode) {
    const tpTag = Object.values(window.allRemarkTags || {}).find(
      (tag) => tag.talkingPoint
    )
    if (tpTag) {
      store.commit('SET_WORKFLOW_REVIEW_ACTIVE_TAG', tpTag)
    }
  }
}

function unmountApp() {
  if (mountedApp) {
    mountedApp.unmount()
    mountedApp = null
    // Clear any highlighted card state to prevent dimming in the main sidepane
    highlightedCardStore.unsetHighlightedCard()
  }
}

onMounted(() => {
  mountCommunicationApp()
})

// Re-mount when context or ledger changes
watch(
  () => [props.contextType, props.contextId, props.ledgerId],
  () => {
    mountCommunicationApp()
  }
)

onBeforeUnmount(() => {
  unmountApp()
})
</script>
